import React, {useContext, useEffect, useState} from "react";
import {AiAttributeServiceContext, AiExtractorServiceContext, AiPromptServiceContext, AiProviderConnectionServiceContext, AppContextContext, ItemUpdateProfileServiceContext, OrganizationServiceContext, VulSourceServiceContext} from "../../Contexts";
import {Alert, AutoComplete, Breadcrumb, Button, Descriptions, Form, Input, message, Modal, Select, Skeleton, Spin, Switch, Tag, Tooltip} from "antd";
import {InfoCircleFilled, LoadingOutlined} from "@ant-design/icons";
import * as globalStyles from "../App.module.css";
import * as styles from "./AiExtractorDetail.module.css";
import {Link, useNavigate, useParams} from "react-router-dom";
import MarkdownContent from "../common/MarkdownContent";
import {DocumentTitle} from "../DocumentTitle";
import AiExtractor, {
    AiExtractorAiAttribute,
    AiExtractorType,
    AiExtractorVulSource,
    AiExtractorWebSearchMode,
    AnthropicReasoningEffort,
    ExternalWebSearchBackend,
    formatAiExtractorType,
    formatAiExtractorWebSearchMode,
    formatExternalWebSearchBackend,
    GrokReasoningEffort,
    isActivelyManagedByInterests,
    OpenAiReasoningEffort,
    OpenAiServiceTier,
    SupportedModel
} from "../../domain/AiExtractor";
import AiProviderConnection from "../../domain/AiProviderConnection";
import {AiExtractorSource, formatAiExtractorSource} from "../../domain/AiExtractorSource";
import {useForm, useWatch} from "antd/es/form/Form";
import QueryOptions from "../../sal-ui/QueryOptions";
import AiAttribute from "../../domain/AiAttribute";
import AiPrompt from "../../domain/AiPrompt";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import Organization from "../../domain/Organization";
import {Permission} from "../../domain/auth/Permission";
import VulSource from "../../domain/VulSource";
import {AiExtractorWebSearchFields, buildValuesForTypeChange, getSupportedModelsForType, isWebSearchSupportedForModel} from "./AiExtractorWebSearch";
import ItemUpdateProfile, {itemUpdateProfileSystemLastComparator} from "../../domain/ItemUpdateProfile";
import {describeUpdatedItemsBehaviour, formatUpdatedItemsBehaviour, UpdatedItemsBehaviour} from "../../domain/UpdatedItemsBehaviour";
import * as descriptionStyles from "../SelectItemDescription.module.css";
import {buildAiPromptSelectOptions} from "../aiprompt/AiPromptSelectOptions";
import {definedValues, orderSystemAndOrganizationOptionGroups} from "../../utils/SelectOptionUtils";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";

const SYSTEM_EXTRACTOR_AUTORUN_DESCRIPTION =
    "Runs this system extractor on items from organization sources when the organization enables system extractor autorun. Manual assignments still work.";

function AiExtractorDetail() {
    const appContext = useContext(AppContextContext);
    const aiExtractorService = useContext(AiExtractorServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const aiProviderConnectionService = useContext(AiProviderConnectionServiceContext);
    const aiPromptService = useContext(AiPromptServiceContext);
    const aiAttributeService = useContext(AiAttributeServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const itemUpdateProfileService = useContext(ItemUpdateProfileServiceContext);
    const navigate = useNavigate();
    const {aiExtractorId} = useParams<{ aiExtractorId: string }>();
    const [aiExtractor, setAiExtractor] = useState<AiExtractor | undefined>();
    const hasManage = appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG);
    const [type, setType] = useState<AiExtractorType | undefined>();
    const [allSupportedModels, setAllSupportedModels] = useState<Record<string, { models: SupportedModel[] }>>({});
    const supportedModels = getSupportedModelsForType(allSupportedModels, type);
    const aiExtractorSupportedModels = getSupportedModelsForType(allSupportedModels, aiExtractor?.type);
    const allowedTypes = Object.keys(allSupportedModels) as AiExtractorType[];
    const [availableAiAttributes, setAvailableAiAttributes] = useState<AiAttribute[]>();
    const [availableAiPrompts, setAvailableAiPrompts] = useState<AiPrompt[]>([]);
    const [availableAiProviderConnections, setAvailableAiProviderConnections] = useState<AiProviderConnection[]>();
    const [itemUpdateProfiles, setItemUpdateProfiles] = useState<ItemUpdateProfile[]>();
    const [editMode, setEditMode] = useState(false);
    const [allVulSources, setAllVulSources] = useState<VulSource[] | undefined>();
    const [selectedVulSourceIds, setSelectedVulSourceIds] = useState<string[]>([]);
    const [vulSourceEditScope, setVulSourceEditScope] = useState<ResourceOwnerType | undefined>();
    const [vulSourceEditMode, setVulSourceEditMode] = useState(false);
    const [vulSourceSaving, setVulSourceSaving] = useState(false);
    const assignedVulSources = aiExtractor?.vulSources || [];
    const systemAssignedVulSources = assignedVulSources.filter(vulSource => vulSource.scope === ResourceOwnerType.SYSTEM);
    const organizationAssignedVulSources = assignedVulSources.filter(vulSource => vulSource.scope === ResourceOwnerType.ORGANIZATION);

    const [organizations, setOrganizations] = useState<Organization[]>();
    const [form] = useForm();
    const watchedOwner = useWatch("owner", form);
    const watchedOrganizationId = useWatch("organization", form);
    const owner = watchedOwner ?? aiExtractor?.owner;
    const organizationId = watchedOrganizationId ?? aiExtractor?.organization?.id;
    const ownershipEditable = !aiExtractorId;
    const selectedSource = useWatch("source", form);
    const selectedModel = useWatch("model", form);
    const selectedWebSearchMode = useWatch("webSearchMode", form);
    const selectedAiProviderConnectionId = useWatch("aiProviderConnectionId", form);
    const updatedItemsBehaviour = useWatch("updatedItemsBehaviour", form);
    const usesAttributeInputInForm = appContext.user.isSystemAdmin()
        ? selectedSource === AiExtractorSource.ATTRIBUTES
        : true;
    const usesAttributeInputInDetail = appContext.user.isSystemAdmin()
        ? aiExtractor?.source === AiExtractorSource.ATTRIBUTES
        : true;
    const selectedSupportedModel = supportedModels.find(model => model.name === selectedModel);
    const aiExtractorSupportedModel = aiExtractorSupportedModels.find(model => model.name === aiExtractor?.model);
    const webSearchSupportedBySelectedModel = isWebSearchSupportedForModel(type, selectedModel, selectedSupportedModel);
    const webSearchSupportedByAiExtractorModel = isWebSearchSupportedForModel(aiExtractor?.type, aiExtractor?.model, aiExtractorSupportedModel);
    const isInterestManaged = isActivelyManagedByInterests(aiExtractor);
    const canDirectlyManageExtractor = !!aiExtractor
        && hasManage
        && !isInterestManaged
        && (appContext.user.isSystemAdmin() || aiExtractor.owner === ResourceOwnerType.ORGANIZATION);
    const canReadAiPrompts = appContext.user.hasOneOfPermission([
        Permission.AI_PROMPT__READ_SEAL,
        Permission.AI_PROMPT__READ_ORG,
        Permission.AI_PROMPT__MANAGE_ORG
    ]);
    const canEditManagedModelConfiguration = !!aiExtractor && appContext.user.isSystemAdmin() && isInterestManaged;
    const showEditForm = (!aiExtractorId || editMode) && (!isInterestManaged || canEditManagedModelConfiguration);
    const editModelConfigurationOnly = canEditManagedModelConfiguration && editMode;
    const showSystemAutoRunSetting = appContext.user.isSystemAdmin() && owner === ResourceOwnerType.SYSTEM && !editModelConfigurationOnly;
    const managedExtractorDescription = appContext.user.isSystemAdmin()
        ? "This AI extractor is maintained by the Interests feature. System administrator can still edit model configuration here, but structural changes remain managed from interests."
        : <>This AI extractor is maintained by the Interests feature. Manage interests instead in <Link to="/interests">interests</Link>.</>;

    const layout = {
        labelCol: {span: 8},
        wrapperCol: {span: 16},
    };

    const tailLayout = {
        wrapperCol: {offset: 8, span: 16},
    };

    const title = aiExtractorId ? (aiExtractor?.name || "AI extractor") : "New AI extractor";
    const visibleAiAttributesForCurrentScope = getVisibleAiAttributesForCurrentScope();

    useEffect(() => {
        if (appContext.user.isSystemAdmin()) {
            organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => setOrganizations(value.data));
        }

        if (aiExtractorId) {
            setEditMode(false);
            reload();
        } else {
            form.setFieldsValue({
                owner: ResourceOwnerType.SYSTEM,
                updatedItemsBehaviour: UpdatedItemsBehaviour.ALWAYS
            });
            setEditMode(true);
        }
    }, [aiExtractorId]);

    useEffect(() => {
        if (!canReadAiPrompts) {
            setAvailableAiPrompts([]);

            return;
        }

        aiPromptService
            .getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setAvailableAiPrompts(result.data));
    }, [aiPromptService, canReadAiPrompts]);

    useEffect(() => {
        setAvailableAiAttributes(undefined);

        aiAttributeService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setAvailableAiAttributes(result.data));
    }, [aiAttributeService]);

    useEffect(() => {
        if (!appContext.user.isSystemAdmin()) {
            setAvailableAiProviderConnections(undefined);

            return;
        }

        aiProviderConnectionService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setAvailableAiProviderConnections(result.data));
    }, [aiProviderConnectionService, appContext.user]);

    useEffect(() => {
        if (!hasManage) {
            setAllVulSources(undefined);

            return;
        }

        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setAllVulSources(result.data));
    }, [hasManage, vulSourceService]);

    useEffect(() => {
        if (!hasManage) {
            setItemUpdateProfiles(undefined);

            return;
        }

        itemUpdateProfileService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setItemUpdateProfiles(result.data?.sort(itemUpdateProfileSystemLastComparator)));
    }, [hasManage, itemUpdateProfileService]);

    useEffect(() => {
        if (availableAiAttributes === undefined) {
            return;
        }

        const selectedAiAttributeIds = form.getFieldValue("aiAttributeIds") as string[] | undefined;

        if (!selectedAiAttributeIds || selectedAiAttributeIds.length === 0) {
            return;
        }

        const [effectiveOwner, effectiveOrganizationId] = resolveEffectiveScope();

        if (!isScopeResolved(effectiveOwner, effectiveOrganizationId)) {
            return;
        }

        const allowedIds = new Set(getVisibleAiAttributesForCurrentScope().map(aiAttribute => aiAttribute.id));
        const filteredSelection = selectedAiAttributeIds.filter(id => allowedIds.has(id));

        if (filteredSelection.length !== selectedAiAttributeIds.length) {
            form.setFieldValue("aiAttributeIds", filteredSelection);
        }
    }, [appContext.user, owner, organizationId, availableAiAttributes, form]);

    useEffect(() => {
        if (availableAiProviderConnections === undefined || !selectedAiProviderConnectionId) {
            return;
        }

        const allowedIds = new Set(getVisibleAiProviderConnectionsForCurrentScope().map(connection => connection.id));

        if (!allowedIds.has(selectedAiProviderConnectionId)) {
            form.setFieldValue("aiProviderConnectionId", undefined);
        }
    }, [appContext.user, owner, organizationId, availableAiProviderConnections, selectedAiProviderConnectionId, isInterestManaged, form]);

    useEffect(() => {
        if (itemUpdateProfiles === undefined) {
            return;
        }

        const selectedProfileId = form.getFieldValue("itemUpdateProfile") as string | undefined;

        if (!selectedProfileId) {
            return;
        }

        const allowedIds = new Set(getVisibleItemUpdateProfilesForCurrentScope().map(profile => profile.id));

        if (!allowedIds.has(selectedProfileId)) {
            form.setFieldValue("itemUpdateProfile", undefined);
        }
    }, [appContext.user, owner, organizationId, itemUpdateProfiles, form]);

    useEffect(() => {
        aiExtractorService.getSupportedModels()
            .then(data => setAllSupportedModels(data));
    }, [aiExtractorService]);

    useEffect(() => {
        if (!selectedSupportedModel) {
            if (type === AiExtractorType.OLLAMA && !selectedModel && supportedModels.length > 0) {
                form.setFieldValue("webSearchMode", AiExtractorWebSearchMode.DISABLED);
            }

            return;
        }

        normalizeOptionalSelection<OpenAiServiceTier>(
            "openAiServiceTier",
            selectedSupportedModel.supportedOpenAiServiceTiers
        );

        normalizeOptionalSelection<OpenAiReasoningEffort>(
            "openAiReasoningEffort",
            selectedSupportedModel.supportedOpenAiReasoningEfforts,
            type === AiExtractorType.OPENAI_API ? selectedSupportedModel.defaultOpenAiReasoningEffort : undefined
        );

        normalizeOptionalSelection<AnthropicReasoningEffort>(
            "anthropicReasoningEffort",
            selectedSupportedModel.supportedAnthropicReasoningEfforts,
            type === AiExtractorType.ANTHROPIC_API ? selectedSupportedModel.defaultAnthropicReasoningEffort : undefined
        );

        normalizeOptionalSelection<GrokReasoningEffort>(
            "grokReasoningEffort",
            selectedSupportedModel.supportedGrokReasoningEfforts,
            type === AiExtractorType.GROK_API ? selectedSupportedModel.defaultGrokReasoningEffort : undefined
        );

        if (selectedWebSearchMode === AiExtractorWebSearchMode.NATIVE && !selectedSupportedModel.supportsWebSearch) {
            form.setFieldValue("webSearchMode", AiExtractorWebSearchMode.DISABLED);
        }
    }, [form, selectedModel, selectedSupportedModel, selectedWebSearchMode, supportedModels.length, type]);

    function normalizeOptionalSelection<T extends string>(
        fieldName: string,
        supportedValues: T[],
        defaultValue?: T
    ) {
        const currentValue = form.getFieldValue(fieldName) as T | undefined;

        if (currentValue && !supportedValues.includes(currentValue)) {
            form.setFieldValue(fieldName, undefined);
        }

        const normalizedValue = form.getFieldValue(fieldName) as T | undefined;

        if (!normalizedValue && defaultValue) {
            form.setFieldValue(fieldName, defaultValue);
        }
    }

    if (aiExtractorId && !aiExtractor) {
        return <Skeleton/>;
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title: <Link to={"/definitions/ai-extractors"}>AI extractors</Link>},
                                {title}
                            ]}
                />

                <Spin
                    spinning={aiExtractorId ? !aiExtractor : false}
                    indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}
                >
                    <h1>{title}</h1>

                    {isInterestManaged &&
                        <Alert
                            className={styles.notice}
                            type="info"
                            showIcon
                            title="Automatically managed by Interests"
                            description={managedExtractorDescription}
                        />
                    }

                    {aiExtractorId && (canDirectlyManageExtractor || canEditManagedModelConfiguration) &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <>
                                <Button onClick={() => setEditMode(!editMode)}>Edit</Button>

                                {canDirectlyManageExtractor &&
                                    <Button danger={true}
                                            title={"Delete"}
                                            onClick={() => {
                                                Modal.confirm({
                                                    content: "Do you really want to delete this AI extractor?",
                                                    okText: "Delete",
                                                    cancelText: "Cancel",
                                                    okButtonProps: {danger: true},
                                                    onOk: () => onDelete()
                                                });
                                            }}>Delete</Button>
                                }
                            </>
                        </div>
                    }

                    {showEditForm && renderEditForm()}

                    {aiExtractorId && aiExtractor &&
                        <div className={styles.details}>
                            <div>
                                <Descriptions
                                    column={1}
                                    bordered={true}
                                    size="small"
                                    className={globalStyles.details}
                                    title={"General"}
                                >
                                    {aiExtractor.owner === ResourceOwnerType.ORGANIZATION &&
                                        <Descriptions.Item label="Owner">{aiExtractor.organization?.name}</Descriptions.Item>}
                                    {aiExtractor.owner === ResourceOwnerType.SYSTEM &&
                                        <Descriptions.Item label="Owner"><i>System AI extractor</i></Descriptions.Item>}
                                    {isInterestManaged &&
                                        <Descriptions.Item label="Management">
                                            Automatically managed from <Link to="/interests">interests</Link>.
                                        </Descriptions.Item>
                                    }
                                    {aiExtractor.owner === ResourceOwnerType.SYSTEM &&
                                        <Descriptions.Item label={
                                            <>
                                                Organization source autorun
                                                {" "}
                                                <Tooltip title={SYSTEM_EXTRACTOR_AUTORUN_DESCRIPTION}>
                                                    <InfoCircleFilled style={{color: "#8c8c8c"}}/>
                                                </Tooltip>
                                            </>
                                        }>
                                            {aiExtractor.autoRunOnOrganizationSources ? "enabled" : "disabled"}
                                        </Descriptions.Item>
                                    }
                                    <Descriptions.Item label="Name">{aiExtractor.name}</Descriptions.Item>
                                    <Descriptions.Item label="Description">
                                        {aiExtractor.description
                                            ? <MarkdownContent children={aiExtractor.description}/>
                                            : <i>no description</i>}
                                    </Descriptions.Item>
                                </Descriptions>

                                <Descriptions
                                    column={1}
                                    bordered={true}
                                    size="small"
                                    className={globalStyles.details}
                                    title={"Model configuration"}
                                >
                                    <Descriptions.Item label="Type">{formatAiExtractorType(aiExtractor.type)}</Descriptions.Item>
                                    {aiExtractor.model &&
                                        <Descriptions.Item label="Model">
                                            {aiExtractor.model}
                                        </Descriptions.Item>
                                    }
                                    {appContext.user.isSystemAdmin() &&
                                        <Descriptions.Item label="Source">
                                            {aiExtractor.source
                                                ? formatAiExtractorSource(aiExtractor.source)
                                                : <i>no source</i>}
                                        </Descriptions.Item>
                                    }
                                    {appContext.user.isSystemAdmin() && aiExtractor.openAiServiceTier &&
                                        <Descriptions.Item label="Service tier">
                                            {String(aiExtractor.openAiServiceTier).toLowerCase()}
                                        </Descriptions.Item>
                                    }
                                    {aiExtractor.aiProviderConnection &&
                                        <Descriptions.Item label="Provider connection">
                                            {appContext.user.isSystemAdmin()
                                                ? <Link to={`/definitions/ai-provider-connections/${aiExtractor.aiProviderConnection.id}`}>
                                                    {aiExtractor.aiProviderConnection.name}
                                                </Link>
                                                : aiExtractor.aiProviderConnection.name}
                                        </Descriptions.Item>
                                    }
                                    {appContext.user.isSystemAdmin() &&
                                        <Descriptions.Item label="Log AI provider calls">
                                            {aiExtractor.aiProviderCommunicationLoggingEnabled ? "enabled" : "disabled"}
                                        </Descriptions.Item>
                                    }
                                    {appContext.user.isSystemAdmin() && (aiExtractor.type === AiExtractorType.OPENAI_API || aiExtractor.type === AiExtractorType.OPENAI_COMPATIBLE_API) &&
                                        <Descriptions.Item label="Reasoning effort">
                                            {aiExtractor.openAiReasoningEffort
                                                ? String(aiExtractor.openAiReasoningEffort).toLowerCase()
                                                : <i>none</i>}
                                        </Descriptions.Item>
                                    }
                                    {appContext.user.isSystemAdmin() && aiExtractor.type === AiExtractorType.ANTHROPIC_API && aiExtractor.anthropicReasoningEffort &&
                                        <Descriptions.Item label="Reasoning effort">
                                            {String(aiExtractor.anthropicReasoningEffort).toLowerCase()}
                                        </Descriptions.Item>
                                    }
                                    {appContext.user.isSystemAdmin() && aiExtractor.type === AiExtractorType.GROK_API && aiExtractor.grokReasoningEffort &&
                                        <Descriptions.Item label="Reasoning effort">
                                            {String(aiExtractor.grokReasoningEffort).toLowerCase()}
                                        </Descriptions.Item>
                                    }
                                    {(aiExtractor.webSearchMode !== AiExtractorWebSearchMode.DISABLED || webSearchSupportedByAiExtractorModel) &&
                                        <Descriptions.Item label="Web search">
                                            {aiExtractor.webSearchMode
                                                ? formatAiExtractorWebSearchMode(aiExtractor.webSearchMode)
                                                : "disabled"}
                                        </Descriptions.Item>
                                    }
                                    {aiExtractor.webSearchMode === AiExtractorWebSearchMode.EXTERNAL &&
                                        <Descriptions.Item label="Web search backend">
                                            {formatExternalWebSearchBackend(aiExtractor.externalWebSearchBackend)}
                                        </Descriptions.Item>
                                    }
                                </Descriptions>
                            </div>

                            <div>
                                <Descriptions
                                    column={1}
                                    bordered={true}
                                    size="small"
                                    className={globalStyles.details}
                                    title={"AI extraction"}
                                >
                                    {(aiExtractor.systemPrompt || aiExtractor.supplementaryPrompt || appContext.user.isSystemAdmin()) && <>
                                        <Descriptions.Item label="Main prompt">
                                            {aiExtractor.systemPrompt?.name || <i>default</i>}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Additional instructions">
                                            {aiExtractor.supplementaryPrompt?.name || <i>no additional instructions</i>}
                                        </Descriptions.Item>
                                    </>}
                                    <Descriptions.Item label="AI attributes">
                                        {renderAiAttributeList(aiExtractor.aiAttributes)}
                                    </Descriptions.Item>
                                    {usesAttributeInputInDetail && <>
                                        <Descriptions.Item label="Re-extract updated items">
                                            {formatUpdatedItemsBehaviour(aiExtractor.updatedItemsBehaviour)}
                                        </Descriptions.Item>
                                        {aiExtractor.updatedItemsBehaviour === UpdatedItemsBehaviour.PROFILE &&
                                            <Descriptions.Item label="Item update profile">
                                                <Link to={`/definitions/item-update-profiles/${aiExtractor.itemUpdateProfile?.id}`}>{aiExtractor.itemUpdateProfile?.name}</Link>
                                            </Descriptions.Item>
                                        }
                                    </>}
                                </Descriptions>

                                {!isInterestManaged && aiExtractor && <>
                                    {aiExtractor.owner === ResourceOwnerType.SYSTEM &&
                                        renderVulSourceAssignmentSection("Assignments by system", ResourceOwnerType.SYSTEM, systemAssignedVulSources)}
                                    {renderVulSourceAssignmentSection("Assignments by organization", ResourceOwnerType.ORGANIZATION, organizationAssignedVulSources)}
                                </>}
                            </div>
                        </div>
                    }
                </Spin>
            </>
        </DocumentTitle>
    );

    function renderEditForm() {
        return (
            <>
                {aiExtractorId && <h3>{editModelConfigurationOnly ? "Edit AI extractor model configuration" : "Edit AI extractor"}</h3>}

                <Form
                    {...layout}
                    form={form}
                    onFinish={onFinish}
                    className={`${globalStyles["common__form"]} ${styles["edit-form"]} ${aiExtractorId ? globalStyles["common_form--edit"] : globalStyles["common_form--add"]}`}
                >
                    {!editModelConfigurationOnly && <>
                        <Form.Item name="name" label="Name" rules={[{required: true, message: "Name is required."}]}>
                            <Input maxLength={100}/>
                        </Form.Item>

                        <Form.Item name="description" label="Description">
                            <Input.TextArea rows={3} maxLength={1000}/>
                        </Form.Item>
                    </>}

                    {appContext.user.isSystemAdmin() && !editModelConfigurationOnly && <>
                        <Form.Item
                            name="owner"
                            label="Owner"
                            rules={[{required: true, message: "Owner is required."}]}
                        >
                            <Select
                                disabled={!ownershipEditable}
                                options={[{
                                    value: ResourceOwnerType.SYSTEM,
                                    label: "system"
                                }, {
                                    value: ResourceOwnerType.ORGANIZATION,
                                    label: "organization"
                                }]}
                            />
                        </Form.Item>

                        {owner === ResourceOwnerType.ORGANIZATION && <Form.Item
                            name="organization"
                            label="Organization"
                            rules={[{required: true, message: "Organization is required."}]}>
                            <Select
                                allowClear={true}
                                disabled={!ownershipEditable}
                                options={organizations?.map(organization => ({value: organization.id, label: organization.name}))}
                            />
                        </Form.Item>}

                        {showSystemAutoRunSetting &&
                            <Form.Item
                                name="autoRunOnOrganizationSources"
                                label="Autorun on organization sources"
                                valuePropName="checked"
                                extra={SYSTEM_EXTRACTOR_AUTORUN_DESCRIPTION}
                            >
                                <Switch/>
                            </Form.Item>
                        }
                    </>}

                    {appContext.user.isSystemAdmin() && !editModelConfigurationOnly &&
                        <Form.Item
                            name="source"
                            label="Source"
                            rules={[{required: true, message: "Source is required."}]}
                            initialValue={AiExtractorSource.JSON_DATA}
                        >
                            <Select>
                                {Object.values(AiExtractorSource).map(source => (
                                    <Select.Option key={source} value={source}>
                                        {formatAiExtractorSource(source)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    }

                    <Form.Item name="type" label="Type" rules={[{required: true, message: "Type is required."}]}>
                        <Select
                            onChange={(value: AiExtractorType) => {
                                setType(value);
                                form.setFieldsValue(buildValuesForTypeChange());
                            }}
                        >
                            {allowedTypes.map(type => (
                                <Select.Option key={type} value={type}>
                                    {formatAiExtractorType(type)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {type &&
                        <>
                            <Form.Item
                                name="model"
                                label="Model"
                                rules={[{required: true, message: "Model is required."}]}
                            >
                                {type === AiExtractorType.OLLAMA || type === AiExtractorType.OPENAI_COMPATIBLE_API ? (
                                    <AutoComplete
                                        options={supportedModels.map(model => ({label: model.name, value: model.name}))}
                                        filterOption={(inputValue, option) => {
                                            if (!inputValue) {
                                                return true;
                                            }

                                            return String(option?.value || "").toLowerCase().includes(inputValue.toLowerCase());
                                        }}
                                        placeholder={"Select or type model"}
                                    >
                                        <Input allowClear maxLength={100}/>
                                    </AutoComplete>
                                ) : (
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        options={supportedModels.map(model => ({label: model.name, value: model.name}))}
                                        placeholder={"Select model"}
                                    />
                                )}
                            </Form.Item>

                            {type === AiExtractorType.OPENAI_COMPATIBLE_API &&
                                <Form.Item
                                    name="aiProviderConnectionId"
                                    label="Provider connection"
                                    rules={[{required: true, message: "Provider connection is required."}]}
                                >
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder={"Select provider connection"}
                                        options={buildAiProviderConnectionSelectOptions()}
                                    />
                                </Form.Item>
                            }

                            {appContext.user.isSystemAdmin() && type === AiExtractorType.OPENAI_API &&
                                <Form.Item
                                    name="openAiServiceTier"
                                    label="Service tier"
                                >
                                    <Select
                                        allowClear
                                        options={buildOptionListWithDefault(
                                            selectedSupportedModel?.supportedOpenAiServiceTiers || []
                                        )}
                                        placeholder={"Select service tier"}
                                    />
                                </Form.Item>
                            }

                            {appContext.user.isSystemAdmin() && (type === AiExtractorType.OPENAI_API || type === AiExtractorType.OPENAI_COMPATIBLE_API) &&
                                <Form.Item
                                    name="openAiReasoningEffort"
                                    label="Reasoning effort"
                                    rules={type === AiExtractorType.OPENAI_API
                                        ? [{required: true, message: "Reasoning effort is required."}]
                                        : []}
                                >
                                    <Select
                                        allowClear
                                        options={type === AiExtractorType.OPENAI_API
                                            ? buildOptionListWithDefault(
                                                selectedSupportedModel?.supportedOpenAiReasoningEfforts || [],
                                                selectedSupportedModel?.defaultOpenAiReasoningEffort
                                            )
                                            : buildOptionListWithDefault(Object.values(OpenAiReasoningEffort))}
                                        placeholder={"Select reasoning effort"}
                                    />
                                </Form.Item>
                            }

                            {appContext.user.isSystemAdmin() && type === AiExtractorType.ANTHROPIC_API && (selectedSupportedModel?.supportedAnthropicReasoningEfforts?.length ?? 0) > 0 &&
                                <Form.Item
                                    name="anthropicReasoningEffort"
                                    label="Reasoning effort"
                                >
                                    <Select
                                        allowClear
                                        options={buildOptionListWithDefault(
                                            selectedSupportedModel?.supportedAnthropicReasoningEfforts || [],
                                            selectedSupportedModel?.defaultAnthropicReasoningEffort
                                        )}
                                        placeholder={"Select reasoning effort"}
                                    />
                                </Form.Item>
                            }

                            {appContext.user.isSystemAdmin() && type === AiExtractorType.GROK_API && (selectedSupportedModel?.supportedGrokReasoningEfforts?.length ?? 0) > 0 &&
                                <Form.Item
                                    name="grokReasoningEffort"
                                    label="Reasoning effort"
                                >
                                    <Select
                                        allowClear
                                        options={buildOptionListWithDefault(
                                            selectedSupportedModel?.supportedGrokReasoningEfforts || [],
                                            selectedSupportedModel?.defaultGrokReasoningEffort
                                        )}
                                        placeholder={"Select reasoning effort"}
                                    />
                                </Form.Item>
                            }

                            <AiExtractorWebSearchFields
                                selectedModel={selectedModel}
                                selectedWebSearchMode={selectedWebSearchMode}
                                type={type}
                                webSearchSupportedBySelectedModel={webSearchSupportedBySelectedModel}
                            />

                            {appContext.user.isSystemAdmin() &&
                                <Form.Item
                                    name="aiProviderCommunicationLoggingEnabled"
                                    label="Log AI provider calls"
                                    valuePropName="checked"
                                >
                                    <Switch/>
                                </Form.Item>
                            }

                            {!editModelConfigurationOnly && renderPromptFields()}

                            {!editModelConfigurationOnly &&
                                <>
                                    <Form.Item
                                        name="aiAttributeIds"
                                        label="AI attributes"
                                        rules={[{required: true, message: "At least one AI attribute is required."}]}
                                    >
                                        <SelectAllMultiSelect
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Select one or more AI attributes"
                                            selectAllValues={definedValues(visibleAiAttributesForCurrentScope, aiAttribute => aiAttribute.id)}
                                            options={visibleAiAttributesForCurrentScope.map(aiAttribute => ({
                                                value: aiAttribute.id!,
                                                label: aiAttribute.name || aiAttribute.id
                                            }))}
                                        />
                                    </Form.Item>

                                    {usesAttributeInputInForm && <>
                                        <Form.Item
                                            name="updatedItemsBehaviour"
                                            label="Re-extract updated items"
                                            rules={[{required: true, message: "Re-extract updated items setting is required."}]}>
                                            <Select
                                                optionLabelProp="label"
                                                popupMatchSelectWidth={500}>
                                                {Object.values(UpdatedItemsBehaviour).map(value =>
                                                    <Select.Option key={value} value={value} label={formatUpdatedItemsBehaviour(value)}>
                                                        <span>{formatUpdatedItemsBehaviour(value)}</span>
                                                        <span className={descriptionStyles.description}> - {describeUpdatedItemsBehaviour(value, "aiExtractor")}</span>
                                                    </Select.Option>
                                                )}
                                            </Select>
                                        </Form.Item>

                                        {updatedItemsBehaviour === UpdatedItemsBehaviour.PROFILE &&
                                            <Form.Item
                                                name="itemUpdateProfile"
                                                label="Item update profile"
                                                rules={[{required: true, message: "Item update profile is required."}]}>
                                                <Select
                                                    showSearch
                                                    optionFilterProp="label"
                                                    options={getVisibleItemUpdateProfilesForCurrentScope().map(profile => ({
                                                        value: profile.id,
                                                        label: profile.name
                                                    }))}
                                                />
                                            </Form.Item>
                                        }
                                    </>}
                                </>
                            }
                        </>
                    }

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type="primary" htmlType="submit">
                            Save
                        </Button>
                        <Button
                            style={{marginLeft: 8}}
                            onClick={() => {
                                if (aiExtractorId) {
                                    setEditMode(false);
                                    if (aiExtractor) {
                                        form.setFieldsValue(toFormValues(aiExtractor));
                                    }
                                } else {
                                    navigate("/definitions/ai-extractors");
                                }
                            }}>
                            Cancel
                        </Button>
                    </Form.Item>
                </Form>
            </>
        );
    }

    function reload() {
        if (!aiExtractorId) {
            return;
        }

        return aiExtractorService.get(aiExtractorId)
            .then(value => {
                setAiExtractor(value);
                setType(value.type);

                form.setFieldsValue(toFormValues(value));
            });
    }

    function toFormValues(value: AiExtractor) {
        return {
            ...value,
            webSearchMode: value.webSearchMode || AiExtractorWebSearchMode.DISABLED,
            externalWebSearchBackend: value.externalWebSearchBackend || ExternalWebSearchBackend.BRAVE,
            systemPromptId: value.systemPrompt?.id,
            supplementaryPromptId: value.supplementaryPrompt?.id,
            aiAttributeIds: (value.aiAttributes || [])
                .map(aiAttribute => aiAttribute.id)
                .filter((id): id is string => !!id),
            organization: value.organization?.id,
            autoRunOnOrganizationSources: value.autoRunOnOrganizationSources,
            aiProviderConnectionId: value.aiProviderConnection?.id,
            updatedItemsBehaviour: value.updatedItemsBehaviour || UpdatedItemsBehaviour.ALWAYS,
            itemUpdateProfile: value.itemUpdateProfile?.id
        };
    }

    function renderPromptFields() {
        const aiPromptOptions = buildAiPromptSelectOptions(
            getVisibleAiPromptsForCurrentScope(),
            appContext.user.isSystemAdmin()
        );

        return <>
            <Form.Item
                name="systemPromptId"
                label="Main prompt"
            >
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select main prompt"
                    options={aiPromptOptions}
                />
            </Form.Item>

            <Form.Item
                name="supplementaryPromptId"
                label="Additional instructions"
            >
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select additional instructions"
                    options={aiPromptOptions}
                />
            </Form.Item>
        </>;
    }

    function getVisibleAiAttributesForCurrentScope() {
        if (availableAiAttributes === undefined) {
            return [];
        }

        const [effectiveOwner, effectiveOrganizationId] = resolveEffectiveScope();

        if (!effectiveOwner) {
            return availableAiAttributes;
        }

        if (effectiveOwner === ResourceOwnerType.SYSTEM) {
            return availableAiAttributes.filter(aiAttribute => aiAttribute.owner === ResourceOwnerType.SYSTEM);
        }

        if (effectiveOwner === ResourceOwnerType.ORGANIZATION && !effectiveOrganizationId) {
            return availableAiAttributes;
        }

        if (effectiveOwner === ResourceOwnerType.ORGANIZATION) {
            return availableAiAttributes.filter(aiAttribute =>
                aiAttribute.owner === ResourceOwnerType.ORGANIZATION && aiAttribute.organization?.id === effectiveOrganizationId
            );
        }

        return [];
    }

    function getVisibleAiPromptsForCurrentScope() {
        const [effectiveOwner, effectiveOrganizationId] = resolveEffectiveScope();

        if (!effectiveOwner) {
            return availableAiPrompts;
        }

        if (effectiveOwner === ResourceOwnerType.SYSTEM) {
            return availableAiPrompts.filter(prompt => prompt.owner === ResourceOwnerType.SYSTEM);
        }

        return availableAiPrompts.filter(prompt =>
            prompt.owner === ResourceOwnerType.SYSTEM
            || (effectiveOrganizationId
                && prompt.owner === ResourceOwnerType.ORGANIZATION
                && prompt.organization?.id === effectiveOrganizationId)
        );
    }

    function getVisibleItemUpdateProfilesForCurrentScope() {
        if (itemUpdateProfiles === undefined) {
            return [];
        }

        const [effectiveOwner, effectiveOrganizationId] = resolveEffectiveScope();

        if (!effectiveOwner) {
            return itemUpdateProfiles;
        }

        if (effectiveOwner === ResourceOwnerType.SYSTEM) {
            return itemUpdateProfiles.filter(profile => profile.owner === ResourceOwnerType.SYSTEM);
        }

        if (effectiveOwner === ResourceOwnerType.ORGANIZATION && !effectiveOrganizationId) {
            return itemUpdateProfiles.filter(profile => profile.owner === ResourceOwnerType.SYSTEM);
        }

        if (effectiveOwner === ResourceOwnerType.ORGANIZATION) {
            return itemUpdateProfiles.filter(profile =>
                profile.owner === ResourceOwnerType.SYSTEM
                || (profile.owner === ResourceOwnerType.ORGANIZATION && profile.organization?.id === effectiveOrganizationId)
            );
        }

        return [];
    }

    function resolveEffectiveScope() {
        const effectiveOwner = appContext.user.isSystemAdmin() ? owner : ResourceOwnerType.ORGANIZATION;
        const effectiveOrganizationId = appContext.user.isSystemAdmin() ? organizationId : appContext.user.organization?.id;

        return [effectiveOwner, effectiveOrganizationId] as const;
    }

    function isScopeResolved(effectiveOwner?: ResourceOwnerType, effectiveOrganizationId?: string) {
        if (!effectiveOwner) {
            return false;
        }

        return effectiveOwner !== ResourceOwnerType.ORGANIZATION || !!effectiveOrganizationId;
    }

    function buildOptionListWithDefault<T extends string>(values: T[], defaultValue?: T) {
        return values.map(value => ({
            value,
            label: value === defaultValue
                ? <span>{value.toLowerCase()} <i>(default)</i></span>
                : value.toLowerCase()
        }));
    }

    function onFinish(values: any) {
        const payload = editModelConfigurationOnly && aiExtractor
            ? buildManagedModelConfigurationPayload(aiExtractor, values)
            : normalizeFormValues(values);

        if (aiExtractorId) {
            aiExtractorService.update(aiExtractorId, payload)
                .then(() => {
                    message.success("AI extractor updated.");

                    setEditMode(false);

                    reload();
                });
        } else {
            aiExtractorService.add(payload)
                .then(id => {
                    message.success("AI extractor added.");

                    navigate(`/definitions/ai-extractors/${id}`);
                });
        }
    }

    function normalizeFormValues(values: any) {
        const effectiveSource = appContext.user.isSystemAdmin()
            ? values.source
            : AiExtractorSource.ATTRIBUTES;
        const autoRunOnOrganizationSources =
            values.owner === ResourceOwnerType.SYSTEM && !!values.autoRunOnOrganizationSources;

        if (effectiveSource !== AiExtractorSource.ATTRIBUTES) {
            return {
                ...values,
                autoRunOnOrganizationSources,
                updatedItemsBehaviour: UpdatedItemsBehaviour.ALWAYS,
                itemUpdateProfile: null
            };
        }

        return {
            ...values,
            autoRunOnOrganizationSources,
            updatedItemsBehaviour: values.updatedItemsBehaviour || UpdatedItemsBehaviour.ALWAYS,
            itemUpdateProfile: values.updatedItemsBehaviour === UpdatedItemsBehaviour.PROFILE ? values.itemUpdateProfile || null : null
        };
    }

    function getVisibleAiProviderConnectionsForCurrentScope() {
        if (availableAiProviderConnections === undefined) {
            return [];
        }

        const [effectiveOwner, effectiveOrganizationId] = resolveEffectiveScope();

        if (effectiveOwner === ResourceOwnerType.SYSTEM) {
            return availableAiProviderConnections.filter(connection => connection.owner === ResourceOwnerType.SYSTEM);
        }

        if (effectiveOwner === ResourceOwnerType.ORGANIZATION && effectiveOrganizationId) {
            return availableAiProviderConnections.filter(connection =>
                (connection.owner === ResourceOwnerType.ORGANIZATION && connection.organization?.id === effectiveOrganizationId)
                || (isInterestManaged && connection.owner === ResourceOwnerType.SYSTEM)
            );
        }

        return [];
    }

    function buildAiProviderConnectionSelectOptions() {
        const connections = getVisibleAiProviderConnectionsForCurrentScope();
        const organizationConnections = connections.filter(connection => connection.owner === ResourceOwnerType.ORGANIZATION);
        const systemConnections = connections.filter(connection => connection.owner === ResourceOwnerType.SYSTEM);
        const groupedOrganizationConnections = new Map<string, AiProviderConnection[]>();

        for (const connection of organizationConnections) {
            const orgName = connection.organization?.name || "Unknown";
            const current = groupedOrganizationConnections.get(orgName) || [];

            current.push(connection);
            groupedOrganizationConnections.set(orgName, current);
        }

        const organizationOptionGroups = [...groupedOrganizationConnections.entries()]
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([orgName, orgConnections]) => ({
                label: orgName,
                options: orgConnections.map(connection => ({
                    value: connection.id!,
                    label: connection.name || connection.id
                }))
            }));
        const systemOptionGroups = systemConnections.length > 0
            ? [{
                label: "System",
                options: systemConnections.map(connection => ({
                    value: connection.id!,
                    label: connection.name || connection.id
                }))
            }]
            : [];

        return orderSystemAndOrganizationOptionGroups(
            systemOptionGroups,
            organizationOptionGroups,
            appContext.user.isSystemAdmin()
        );
    }

    function onSaveVulSources() {
        if (!aiExtractor) {
            return;
        }

        if (!vulSourceEditScope) {
            return;
        }

        const scope = vulSourceEditScope === ResourceOwnerType.SYSTEM ? 'system' : 'organization';

        setVulSourceSaving(true);

        aiExtractorService.updateVulSources(aiExtractorId!, selectedVulSourceIds, scope)
            .then(() => {
                message.success("Sources updated.");

                return reload();
            })
            .then(closeVulSourceEditor)
            .finally(() => setVulSourceSaving(false));
    }

    function closeVulSourceEditor() {
        setVulSourceEditMode(false);
        setVulSourceEditScope(undefined);
    }

    function renderVulSourceList(vulSources: AiExtractorVulSource[]) {
        if (vulSources.length === 0) {
            return <div><i>no sources</i></div>;
        }

        return (
            <div className={styles.assignmentList}>
                {vulSources.map(vulSource => (
                    <div key={vulSource.id} className={styles.assignment}>
                        <Tag
                            variant={"filled"}
                            color={assignedVulSourceOwnerColor(vulSource)}
                            className={styles.assignmentTag}
                            title={vulSource.owner === ResourceOwnerType.SYSTEM ? "System source" : "Organization source"}>
                            {formatAssignedVulSourceOwner(vulSource)}
                        </Tag>
                        <Link to={`/vul-sources/${vulSource.id}`}>{vulSource.name}</Link>
                        {renderAssignedVulSourceOrganization(vulSource)}
                    </div>
                ))}
            </div>
        );
    }

    function renderAiAttributeList(aiAttributes?: AiExtractorAiAttribute[]) {
        if (!aiAttributes || aiAttributes.length === 0) {
            return <div><i>no AI attributes</i></div>;
        }

        return (
            <div>
                {aiAttributes.map(aiAttribute => (
                    <div key={aiAttribute.id || aiAttribute.name}>
                        {aiAttribute.id
                            ? <Link to={`/definitions/ai-attributes/${aiAttribute.id}`}>{aiAttribute.name || aiAttribute.id}</Link>
                            : (aiAttribute.name || <i>unknown AI attribute</i>)}
                    </div>
                ))}
            </div>
        );
    }

    function formatAssignedVulSourceOwner(vulSource: AiExtractorVulSource) {
        return vulSource.owner === ResourceOwnerType.SYSTEM ? "sys" : "org";
    }

    function assignedVulSourceOwnerColor(vulSource: AiExtractorVulSource) {
        return vulSource.owner === ResourceOwnerType.SYSTEM ? "#440084" : "#0066cc";
    }

    function renderAssignedVulSourceOrganization(vulSource: AiExtractorVulSource) {
        if (!appContext.user.isSystemAdmin()
            || vulSource.owner !== ResourceOwnerType.ORGANIZATION
            || !vulSource.organizationName) {
            return null;
        }

        return <span className={styles.assignmentOrganization}>{vulSource.organizationName}</span>;
    }

    function buildManagedModelConfigurationPayload(existingAiExtractor: AiExtractor, values: any) {
        return {
            ...toFormValues(existingAiExtractor),
            type: values.type,
            model: values.model,
            openAiServiceTier: values.openAiServiceTier,
            openAiReasoningEffort: values.openAiReasoningEffort,
            anthropicReasoningEffort: values.anthropicReasoningEffort,
            grokReasoningEffort: values.grokReasoningEffort,
            webSearchMode: values.webSearchMode,
            externalWebSearchBackend: values.externalWebSearchBackend,
            aiProviderConnectionId: values.aiProviderConnectionId,
            aiProviderCommunicationLoggingEnabled: values.aiProviderCommunicationLoggingEnabled
        };
    }

    function getAssignableVulSources(scope: ResourceOwnerType): VulSource[] {
        if (!allVulSources || !aiExtractor) {
            return [];
        }

        if (scope === ResourceOwnerType.SYSTEM) {
            return allVulSources.filter(s => s.owner === ResourceOwnerType.SYSTEM);
        }

        return allVulSources.filter(s => {
            if (s.owner === ResourceOwnerType.SYSTEM) {
                return aiExtractor.owner === ResourceOwnerType.ORGANIZATION;
            }

            if (s.owner !== ResourceOwnerType.ORGANIZATION) {
                return false;
            }

            return aiExtractor.owner === ResourceOwnerType.SYSTEM
                   || s.organization?.id === aiExtractor.organization?.id;
        });
    }

    function buildVulSourceSelectOptions(scope: ResourceOwnerType): { label: string; options: { label: string; value: string }[] }[] {
        const sources = getAssignableVulSources(scope);
        const systemSources = sources.filter(s => s.owner === ResourceOwnerType.SYSTEM);
        const orgGroups = new Map<string, VulSource[]>();

        for (const source of sources) {
            if (source.owner !== ResourceOwnerType.ORGANIZATION) {
                continue;
            }

            const orgName = source.organization?.name || "Unknown";
            const group = orgGroups.get(orgName);

            if (group) {
                group.push(source);
            } else {
                orgGroups.set(orgName, [source]);
            }
        }

        const systemOptionGroups = systemSources.length > 0 ? [{
            label: "System",
            options: systemSources.map(s => ({label: s.name || "", value: s.id || ""}))
        }] : [];
        const organizationOptionGroups = [...orgGroups.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([orgName, sources]) => ({
                label: orgName,
                options: sources.map(s => ({label: s.name || "", value: s.id || ""}))
            }));

        return orderSystemAndOrganizationOptionGroups(
            systemOptionGroups,
            organizationOptionGroups,
            appContext.user.isSystemAdmin()
        );
    }

    function getVulSourceSelectOptionValues(options: { options: { value: string }[] }[]) {
        return options
            .flatMap(group => group.options.map(option => option.value))
            .filter(Boolean);
    }

    function canEditVulSourceAssignments(scope: ResourceOwnerType) {
        if (!aiExtractor || isInterestManaged || !hasManage) {
            return false;
        }

        if (scope === ResourceOwnerType.SYSTEM) {
            return appContext.user.isSystemAdmin() && aiExtractor.owner === ResourceOwnerType.SYSTEM;
        }

        return appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_EXTRACTOR__MANAGE_ORG);
    }

    function renderVulSourceAssignmentSection(title: string, scope: ResourceOwnerType, vulSources: AiExtractorVulSource[]) {
        const editing = vulSourceEditMode && vulSourceEditScope === scope;
        const editable = canEditVulSourceAssignments(scope);

        return (
            <Descriptions column={1} bordered={true} size="small" className={globalStyles.details} title={title}>
                <Descriptions.Item label={"Sources"}>
                    {!allVulSources ? <Spin size="small"/> : !editing ? (
                        <>
                            {renderVulSourceList(vulSources)}
                            {editable &&
                                <Button size="small" style={{marginTop: 8}} onClick={() => {
                                    setSelectedVulSourceIds(vulSources.flatMap(vs => vs.id ? [vs.id] : []));
                                    setVulSourceEditScope(scope);
                                    setVulSourceEditMode(true);
                                }}>Edit</Button>
                            }
                        </>
                    ) : (
                        renderScopedVulSourceEditor(scope)
                    )}
                </Descriptions.Item>
            </Descriptions>
        );
    }

    function renderScopedVulSourceEditor(scope: ResourceOwnerType) {
        const options = buildVulSourceSelectOptions(scope);

        return (
            <>
                <SelectAllMultiSelect
                    showSearch
                    optionFilterProp="label"
                    style={{width: '100%'}}
                    value={selectedVulSourceIds}
                    onChange={setSelectedVulSourceIds}
                    selectAllValues={getVulSourceSelectOptionValues(options)}
                    options={options}
                />
                <div style={{marginTop: 8}}>
                    <Button type="primary" size="small" loading={vulSourceSaving} onClick={onSaveVulSources}>Save</Button>
                    <Button size="small" style={{marginLeft: 8}} onClick={closeVulSourceEditor}>Cancel</Button>
                </div>
            </>
        );
    }

    function onDelete() {
        aiExtractorService.delete(aiExtractor).then(() => {
            message.success("AI extractor deleted.");

            navigate("/definitions/ai-extractors");
        });
    }

}

export default AiExtractorDetail;
